/**
 * PWA installability (UX-406).
 *
 * The manifest was complete and the phone was still not installable: no service worker was
 * registered (`getRegistrations()` answered 0) and nothing listened for `beforeinstallprompt`,
 * so Chrome on Android had no reason to offer the install and no way for the app to ask. iOS
 * "Add to Home Screen" worked by hand, which is the only route iOS has.
 *
 * Both halves live here. The worker is registered after `load` so it never competes with the
 * first paint, and the deferred prompt is kept so Profile can offer "Instalar la app" at a
 * moment the user chose rather than whenever Chrome felt like it.
 */

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: InstallPrompt | null = null;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to "can this phone be asked to install?" (a `useSyncExternalStore` source). */
export function subscribeInstallPrompt(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function canInstall(): boolean {
  return deferred !== null;
}

/** Shows Chrome's install prompt. Resolves to true when the user accepted it. */
export async function promptInstall(): Promise<boolean> {
  const prompt = deferred;
  if (!prompt) return false;
  // A deferred prompt is single-use, whatever the user answers.
  deferred = null;
  announce();
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  return outcome === 'accepted';
}

export function installPwa(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own mini-infobar and the app never gets to ask.
    event.preventDefault();
    deferred = event as InstallPrompt;
    announce();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    announce();
  });

  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // An unavailable worker costs the install prompt and nothing else; never the app.
    });
  });
}
